import { FC, ReactNode } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, SxProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface ConfirmDialogProps {
  /**
   * Controls whether the dialog is visible
   */
  show: boolean
  /**
   * Callback function when user confirms (clicks OK) - returns true
   */
  onConfirm: () => void
  /**
   * Callback function when user cancels (clicks Cancel, ESC, or backdrop) - returns false
   */
  onCancel: () => void
  /**
   * Title text to display at the top of the dialog
   */
  title?: string
  /**
   * Message content - can be a string or custom React components
   */
  message?: ReactNode
  /**
   * Children components to render inside the dialog content area
   */
  children?: ReactNode
  /**
   * Text for the confirm/OK button (default: 'OK')
   */
  confirmText?: string
  /**
   * Text for the cancel button (default: 'Cancel')
   */
  cancelText?: string
  /**
   * Color variant for the confirm button (default: 'primary')
   */
  confirmColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * Color variant for the cancel button (default: 'secondary')
   */
  cancelColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * Variant for the confirm button (default: 'contained')
   */
  confirmVariant?: 'text' | 'outlined' | 'contained'
  /**
   * Variant for the cancel button (default: 'outlined')
   */
  cancelVariant?: 'text' | 'outlined' | 'contained'
  /**
   * Maximum width of the dialog (default: 'sm')
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  /**
   * Additional styles to apply to the dialog content
   */
  sx?: SxProps
  /**
   * Additional styles to apply to the dialog paper
   */
  paperSx?: SxProps
  /**
   * Content alignment (default: 'center')
   */
  align?: 'left' | 'center' | 'right'
  /**
   * Allow closing via backdrop click (default: true, calls onCancel)
   */
  allowBackdropClose?: boolean
}

const ConfirmDialog: FC<ConfirmDialogProps> = ({
  show,
  onConfirm,
  onCancel,
  title,
  message,
  children,
  confirmText = 'OK',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  cancelColor = 'warning',
  confirmVariant = 'contained',
  cancelVariant = 'outlined',
  maxWidth = 'sm',
  sx,
  paperSx,
  align = 'center',
  allowBackdropClose = true,
}) => {
  const theme = useTheme()

  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && !allowBackdropClose) {
      return
    }
    onCancel()
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: 2,
          ...paperSx,
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            textAlign: align,
            fontSize: '1.5rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          justifyContent: 'center',
          gap: 2,
          py: 3,
          ...sx,
        }}
      >
        {message && (
          <Typography
            variant="body1"
            sx={{
              textAlign: align,
              color: theme.palette.text.secondary,
            }}
          >
            {message}
          </Typography>
        )}
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          gap: 2,
        }}
      >
        <Button variant={cancelVariant} color={cancelColor} onClick={onCancel}>
          {cancelText}
        </Button>
        <Button variant={confirmVariant} color={confirmColor} onClick={onConfirm}>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDialog
